import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ModuleGuide } from "./ModuleGuide";
import { StepBar, Primary, Footer, Num, PropertySelect, Stat, eur } from "./ui";

const LABELS = ["Bien financé", "Votre prêt", "Vérification"];
const firstOfNextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 1); return d.toISOString().slice(0, 10); };

const SplitPicker = ({ properties, splits, setSplits }) => (
  <div className="space-y-2" data-testid="loan-splits">
    {properties.map((p) => {
      const s = splits.find((x) => x.property_id === p.id);
      return (
        <div key={p.id} className="flex items-center gap-3">
          <input type="checkbox" checked={!!s} onChange={(e) => setSplits(e.target.checked ? [...splits, { property_id: p.id, pct: 0 }] : splits.filter((x) => x.property_id !== p.id))} data-testid={`loan-split-check-${p.id}`} />
          <span className="flex-1 text-sm">{p.name}</span>
          {s && <input type="number" className="input w-24" value={s.pct} onChange={(e) => setSplits(splits.map((x) => x.property_id === p.id ? { ...x, pct: Number(e.target.value) } : x))} data-testid={`loan-split-pct-${p.id}`} />}
          {s && <span className="text-xs">%</span>}
        </div>
      );
    })}
    <p className="text-xs text-brand-green/60">Total : {splits.reduce((a, x) => a + (x.pct || 0), 0)} % (doit faire 100 %)</p>
  </div>
);

const Advanced = ({ f, set }) => (
  <div className="mt-4 rounded-2xl bg-brand-softer p-4 space-y-3" data-testid="loan-advanced">
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Différé</span>
        <select className="input" value={f.defer_type} onChange={(e) => set("defer_type", Number(e.target.value))} data-testid="loan-defer-type">
          <option value={1}>Aucun</option><option value={2}>Partiel (intérêts seuls)</option><option value={3}>Total</option>
        </select></label>
      {f.defer_type > 1 && <Num label="Durée du différé" suffix="mois" value={f.defer_months} onChange={(v) => set("defer_months", v)} testid="loan-defer-months" />}
      <Num label="Frais de dossier" suffix="€" optional value={f.fees} onChange={(v) => set("fees", v)} testid="loan-fees" />
      <Num label="Frais de garantie" suffix="€" optional value={f.guarantee_fees} onChange={(v) => set("guarantee_fees", v)} testid="loan-guarantee-fees" />
    </div>
    <p className="text-xs text-brand-green/60">Taux variable ou prêt in fine ? Importez votre tableau d'amortissement depuis la page Prêts (bouton « Importer ») : il sera repris à l'identique.</p>
  </div>
);

const Steps = ({ properties, onDone }) => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [multi, setMulti] = useState(false);
  const [pid, setPid] = useState(properties[0]?.id);
  const [splits, setSplits] = useState([]);
  const [f, setF] = useState({ amount: "", rate: "", duration: 20, unit: "ans", first_date: firstOfNextMonth(), insurance_month: "", defer_type: 1, defer_months: 0, fees: "", guarantee_fees: "" });
  const [adv, setAdv] = useState(false);
  const [prev, setPrev] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const body = () => ({ amount: Number(f.amount), rate: Number(f.rate), months: Number(f.duration) * (f.unit === "ans" ? 12 : 1), first_date: f.first_date,
    insurance_month: Number(f.insurance_month) || 0, defer_type: Number(f.defer_type), defer_months: Number(f.defer_months) || 0, fees: Number(f.fees) || 0, guarantee_fees: Number(f.guarantee_fees) || 0,
    splits: multi ? splits : [{ property_id: pid, pct: 100 }] });
  const err = (e) => toast.error(e?.response?.data?.detail || "Erreur");
  const preview = async () => { setBusy(true); try { setPrev((await api.post("/guides/loans/preview", body())).data); setStep(3); } catch (e) { err(e); } finally { setBusy(false); } };
  const create = async () => { setBusy(true); try { setRes((await api.post("/guides/loans", body())).data); setStep(4); } catch (e) { err(e); } finally { setBusy(false); } };
  const download = async (l) => {
    const r = await api.get(l.export_url.replace(/^\/api/, ""), { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `tableau-amortissement-${l.property}.xlsx`; a.click();
  };
  if (step === 4 && res) return (
    <div className="pb-6" data-testid="loan-success">
      <CheckCircle2 className="w-10 h-10 text-brand-limeDark" />
      <h3 className="font-display text-2xl text-brand-greenDeep mt-3">Votre prêt est enregistré</h3>
      {res.loans.map((l) => (
        <div key={l.id} className="mt-4">
          <p className="text-sm font-semibold text-brand-greenDeep mb-2">{l.property}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Stat label="Capital restant dû aujourd'hui" value={eur(l.remaining_capital)} testid="loan-remaining" />
            <Stat label={`Intérêts payés en ${new Date().getFullYear()}`} value={eur(l.interest_this_year)} testid="loan-interest-year" />
          </div>
          <button onClick={() => download(l)} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-greenDeep underline underline-offset-4" data-testid={`loan-download-${l.id}`}><Download className="w-4 h-4" /> Télécharger le tableau d'amortissement</button>
        </div>
      ))}
      <p className="text-xs text-brand-green/60 mt-4">Les échéances sont ajoutées à vos finances (capital et intérêts séparés). Les intérêts et l'assurance alimentent vos données fiscales.</p>
      <Footer><button onClick={() => { onDone(); nav("/app/finances/bilan?guide=forecast"); }} className="px-4 py-3 rounded-full border border-brand-soft text-sm font-semibold" data-testid="loan-to-forecast">Voir mon prévisionnel</button><Primary onClick={onDone} data-testid="loan-done">Terminer</Primary></Footer>
    </div>
  );
  return (
    <div data-testid="loan-steps">
      <StepBar step={step} total={3} labels={LABELS} />
      {step === 1 && (
        <div className="space-y-4">
          {!multi && <PropertySelect properties={properties} value={pid} onChange={setPid} testid="loan-property-select" />}
          {properties.length > 1 && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} data-testid="loan-multi" /> Ce prêt finance plusieurs biens</label>}
          {multi && <SplitPicker properties={properties} splits={splits} setSplits={setSplits} />}
        </div>
      )}
      {step === 2 && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="loan-fields">
          <Num label="Montant emprunté" suffix="€" value={f.amount} onChange={(v) => set("amount", v)} testid="loan-amount" />
          <Num label="Taux nominal" suffix="%" value={f.rate} onChange={(v) => set("rate", v)} testid="loan-rate" />
          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
            <Num label="Durée" value={f.duration} onChange={(v) => set("duration", v)} testid="loan-duration" />
            <select className="input" value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="loan-duration-unit"><option value="ans">ans</option><option value="mois">mois</option></select>
          </div>
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Date de 1re échéance</span><input type="date" className="input" value={f.first_date} onChange={(e) => set("first_date", e.target.value)} data-testid="loan-first-date" /></label>
          <Num label="Assurance emprunteur" suffix="€/mois" optional value={f.insurance_month} onChange={(v) => set("insurance_month", v)} testid="loan-insurance" />
        </div>
      )}
      {step === 3 && prev && (
        <div data-testid="loan-verify">
          <p className="text-sm text-brand-green/70">Comparez avec votre offre de prêt :</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Stat label="Mensualité calculée (assurance incluse)" value={eur(prev.monthly)} testid="loan-monthly" />
            <Stat label="Coût total des intérêts" value={eur(prev.total_interest)} />
          </div>
          {!adv && <button onClick={() => setAdv(true)} className="mt-4 text-sm font-semibold text-brand-greenDeep underline underline-offset-4" data-testid="loan-different">Ma mensualité est différente</button>}
          {adv && <><Advanced f={f} set={set} /><button onClick={preview} className="mt-3 text-sm font-semibold text-brand-greenDeep underline" data-testid="loan-recompute">Recalculer</button></>}
        </div>
      )}
      <Footer onBack={step > 1 ? () => setStep(step - 1) : null}>
        {step === 1 && <Primary onClick={() => setStep(2)} disabled={multi && splits.reduce((a, x) => a + (x.pct || 0), 0) !== 100} data-testid="loan-next-1">Continuer</Primary>}
        {step === 2 && <Primary busy={busy} onClick={preview} disabled={!f.amount || f.rate === "" || !f.duration} data-testid="loan-next-2">Calculer ma mensualité</Primary>}
        {step === 3 && <Primary busy={busy} onClick={create} data-testid="loan-create">C'est bon, enregistrer</Primary>}
      </Footer>
    </div>
  );
};

const Preview = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
    <Stat label="Mensualité" value="1 012 €" /><Stat label="Intérêts 2026" value="4 380 €" /><Stat label="Capital restant" value="168 400 €" />
  </div>
);

export const LoansGuide = () => (
  <ModuleGuide module="loans" label="Guide Prêt" intro="Ajoutez votre prêt : on calcule tout pour vous."
    benefit="Mensualités, intérêts déductibles et capital restant dû, à partir de 5 informations." preview={<Preview />} Steps={Steps} />
);
